import { RouteObject } from "react-router-dom";
import { FinancesPage } from "src/routes/billing/FinancesPage";
import { InvoiceTermsForm } from "src/routes/billing/invoice-terms/components/InvoiceTermsForm";
import { BillEditor } from "src/routes/bills/BillEditor";
import { NotFound } from "src/routes/NotFound";
import { AppShell } from "src/routing/components/AppShell";
import { BeamNavbarLayout } from "src/routing/components/layouts/BeamNavbarLayout";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { ScrollableParentLayout } from "src/routing/components/layouts/ScrollableParentLayout";
import * as financesPaths from "src/routing/domains/financesPaths";

/**
 * Top-level Finances route trees (`/bills`, `/invoices`, `/purchase-orders`, `/invoice-terms`, `/lien-waivers`).
 *
 * Role: data router domain route.
 *
 * Tab list pages use Beam page layouts:
 * `AppShell` → `BeamNavbarLayout` → `FinancesPage` (`PageHeaderLayout` + `GridTableLayout`).
 *
 * Bill editor and invoice-term form still own their own scroll:
 * `AppShell` → `GlobalNavLayout` → `ScrollableParentLayout` → page.
 *
 * Path constants live in `financesPaths.ts` (imported by `RouteUrls.ts` / pages) to avoid a cycle
 * through this file's page `Component` imports.
 */
const financesTabLayout: RouteObject = {
  Component: BeamNavbarLayout,
  children: [{ index: true, Component: FinancesPage }],
};

const billsDomainRoute: RouteObject = {
  path: financesPaths.billsRoute.path,
  Component: AppShell,
  children: [
    financesTabLayout,
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          // BillEditor also renders BillPage for non-editable bills.
          children: [{ path: financesPaths.createBillRoute.path, Component: BillEditor }],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

const invoicesDomainRoute: RouteObject = {
  path: financesPaths.invoicesRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

const purchaseOrdersDomainRoute: RouteObject = {
  path: financesPaths.purchaseOrdersRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

const invoiceTermsDomainRoute: RouteObject = {
  path: financesPaths.invoiceTermsRoute.path,
  Component: AppShell,
  children: [
    financesTabLayout,
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          children: [{ path: `${financesPaths.invoiceTermRoute.path}/*`, Component: InvoiceTermsForm }],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

const lienWaiversDomainRoute: RouteObject = {
  path: financesPaths.lienWaiversRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

/**
 * Migrated Finances route trees registered in `Router.tsx`.
 */
export const financesRoutes: RouteObject[] = [
  billsDomainRoute,
  invoicesDomainRoute,
  purchaseOrdersDomainRoute,
  invoiceTermsDomainRoute,
  lienWaiversDomainRoute,
];
